import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useUniversities } from "../../hooks/useUniversities";
import Layout from "../layout/Layout";
import "./Auth.css";

// Login のOTP送信メッセージと同トーン（存在有無を示さない）
const REGISTER_EMAIL_GENERIC_HINT =
  "登録を完了できませんでした。入力内容をご確認のうえ、再度お試しください。";

const Register = () => {
  // Form fields
  const [name, setName] = useState("");
  const [studentId, setStudentId] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [faculty, setFaculty] = useState("");
  const [department, setDepartment] = useState("");
  const [grade, setGrade] = useState("");
  const [email, setEmail] = useState("");

  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  // OTP state
  const [step, setStep] = useState("form"); // form or otp
  const [otp, setOTP] = useState("");
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const { signup, verifySignupOTP, currentUser, checkUserExists } = useAuth();
  const navigate = useNavigate();
  const {
    universities,
    loading: universitiesLoading,
    validateEmailDomain,
  } = useUniversities();

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  // Timer countdown for OTP expiry
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else if (timer === 0 && step === "otp") {
      // When timer reaches 0, enable resend
      setCanResend(true);
    }
  }, [timer, step]);

  // Debounced email validation effect
  useEffect(() => {
    if (!universityId || !email || formSubmitted) return;

    const timeoutId = setTimeout(() => {
      // Only validate if email appears to be complete (has @ and proper domain)
      const emailParts = email.split("@");
      if (
        emailParts.length === 2 &&
        emailParts[1].includes(".") &&
        emailParts[1].length > 1
      ) {
        const domain = emailParts[1];
        // Check if domain has content before and after the dot
        if (
          domain.indexOf(".") > 0 &&
          domain.indexOf(".") < domain.length - 1
        ) {
          const isValid = validateEmailDomain(email, universityId);
          if (!isValid) {
            setFieldErrors((prev) => ({
              ...prev,
              email: "選択した大学のメールアドレスを使用してください",
            }));
          } else {
            // Clear email error only when validation passes and it's not a user existence error
            setFieldErrors((prev) => {
              const newErrors = { ...prev };
              // Don't clear user existence errors or other important validation errors
              if (prev.email && prev.email !== REGISTER_EMAIL_GENERIC_HINT) {
                delete newErrors.email;
              }
              return newErrors;
            });
          }
        }
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [email, universityId, validateEmailDomain, formSubmitted]);

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");

    const cleanedOTP = otp.replace(/\D/g, "");

    if (cleanedOTP.length !== 6) {
      setError("6桁のワンタイムパスワードを入力してください");
      return;
    }

    setLoading(true);

    try {
      const result = await verifySignupOTP(email, cleanedOTP);

      if (result.success) {
        setSuccessMessage("登録成功！リダイレクト中...");

        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error) {
      setError(
        "ワンタイムパスワードの確認に失敗しました。入力内容をご確認のうえ、再度お試しください。"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      // Get stored user data
      const pendingSignupStr = sessionStorage.getItem("pendingSignup");
      if (!pendingSignupStr) {
        throw new Error("登録情報が見つかりません");
      }

      const pendingSignup = JSON.parse(pendingSignupStr);
      const result = await signup(pendingSignup.email, pendingSignup.userData);

      if (result.otpSent) {
        setSuccessMessage("新しいワンタイムパスワードを送信しました");
        setTimer(result.expiresIn || 300);
        setCanResend(false);
        setOTP("");
      }
    } catch (error) {
      setError("ワンタイムパスワードの再送信に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setOTP(cleaned);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const validateForm = async () => {
    const errors = {};

    if (!name) errors.name = "氏名を入力してください";
    if (!studentId) errors.studentId = "学籍番号を入力してください";
    if (!universityId) errors.universityId = "大学を選択してください";
    if (!faculty) errors.faculty = "学部を入力してください";
    if (!department) errors.department = "学科を入力してください";
    if (!grade) errors.grade = "学年を選択してください";
    if (!email) {
      errors.email = "メールアドレスを入力してください";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "メールアドレスの形式が正しくありません";
    } else if (!universityId) {
      errors.email = "先に大学を選択してください";
    } else {
      // ドメインチェック
      const isValidDomain = validateEmailDomain(email, universityId);
      if (!isValidDomain) {
        errors.email = "選択した大学のメールアドレスを使用してください";
      } else {
        // メールアドレスの重複チェック
        const userExists = await checkUserExists(email);
        if (userExists) {
          errors.email = REGISTER_EMAIL_GENERIC_HINT;
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set form as submitted to prevent useEffect interference
    setFormSubmitted(true);

    // Validate form first (now async)
    const isValid = await validateForm();
    if (!isValid) {
      setFormSubmitted(false); // Reset if validation fails
      return;
    }

    // Clear previous messages only if validation passed
    setError("");
    setSuccessMessage("");

    try {
      setLoading(true);

      // Get university name
      const university = universities.find((u) => u.id === universityId);

      // Format user data
      const userData = {
        氏名: name,
        学籍番号: studentId,
        在籍大学: university?.name || universityId,
        大学ID: universityId,
        学部: faculty,
        学科: department,
        学年: grade,
        連絡先: email,
        email: email,  // Firestore rulesで必要
      };

      // Register user with OTP
      const result = await signup(email, userData);
      if (result.otpSent) {
        setStep("otp");
        setTimer(result.expiresIn || 300);
        setCanResend(false);
        setSuccessMessage(
          "ワンタイムパスワードを送信しました。メールをご確認ください。届かない場合は、入力内容や迷惑メールフォルダをご確認ください。"
        );
        setLoading(false); // Reset loading state after successful OTP send
        // Keep formSubmitted as true on success to prevent useEffect
      }
    } catch (error) {
      setLoading(false);
      setFormSubmitted(false); // Reset on error to allow re-validation

      // Set error message based on error code
      let message = "登録を完了できませんでした。入力をご確認ください。";
      switch (error.code) {
        case "auth/email-already-in-use":
          message = "登録を完了できませんでした。入力をご確認ください。";
          setFieldErrors((prev) => ({ ...prev, email: message }));
          break;
        case "auth/invalid-email":
          message = "メールアドレスの形式が正しくありません。";
          setFieldErrors((prev) => ({ ...prev, email: message }));
          break;
        case "permission-denied":
          // Firestore security rules error (likely domain validation)
          message =
            "登録を完了できませんでした。入力をご確認ください。";
          setFieldErrors((prev) => ({ ...prev, email: message }));
          break;
        default:
          setError(
            `登録を完了できませんでした。入力をご確認ください。`
          );
      }
    }
  };

  return (
    <Layout>
      <div className="login-box">
        <h2>学生登録</h2>

        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        {step === "form" ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">
                氏名 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {fieldErrors.name && (
                <div className="error-message">{fieldErrors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="studentId">
                学籍番号 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="studentId"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                required
              />
              {fieldErrors.studentId && (
                <div className="error-message">{fieldErrors.studentId}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="universityId">
                大学 <span className="required">*</span>
              </label>
              <select
                id="universityId"
                value={universityId}
                onChange={(e) => setUniversityId(e.target.value)}
                required
                disabled={universitiesLoading}
              >
                <option value="">選択してください</option>
                {universities.map((uni) => (
                  <option key={uni.id} value={uni.id}>
                    {uni.name}
                  </option>
                ))}
              </select>
              {fieldErrors.universityId && (
                <div className="error-message">{fieldErrors.universityId}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="faculty">
                学部 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="faculty"
                value={faculty}
                onChange={(e) => setFaculty(e.target.value)}
                placeholder="例: 工学部、文学部、経済学部など"
                required
              />
              {fieldErrors.faculty && (
                <div className="error-message">{fieldErrors.faculty}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="department">
                学科 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                required
              />
              {fieldErrors.department && (
                <div className="error-message">{fieldErrors.department}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="grade">
                学年 <span className="required">*</span>
              </label>
              <select
                id="grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                required
              >
                <option value="">選択してください</option>
                <option value="1年">1年</option>
                <option value="2年">2年</option>
                <option value="3年">3年</option>
                <option value="4年">4年</option>
                <option value="5年">5年</option>
                <option value="6年">6年</option>
              </select>
              {fieldErrors.grade && (
                <div className="error-message">{fieldErrors.grade}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                メールアドレス <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={
                  universityId
                    ? "選択した大学のメールアドレスを入力"
                    : "大学を選択してください"
                }
                disabled={!universityId}
                required
              />
              {fieldErrors.email && (
                <div className="error-message">{fieldErrors.email}</div>
              )}
            </div>

            <div className="form-group">
              <button type="submit" disabled={loading}>
                {loading ? "登録中..." : "登録"}
              </button>
            </div>

            <div className="form-links">
              <Link to="/login">すでにアカウントをお持ちの方はこちら</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div className="form-group">
              <label htmlFor="otp">ワンタイムパスワード</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={handleOTPChange}
                placeholder="6桁の数字"
                maxLength="6"
                pattern="\d{6}"
                required
                disabled={loading}
                style={{
                  fontSize: "24px",
                  textAlign: "center",
                  letterSpacing: "8px",
                  fontFamily: "monospace"
                }}
              />
              <small style={{ color: "#666", fontSize: "12px", display: "block", marginTop: "8px" }}>
                送信先: {email}
              </small>
            </div>

            <div className="form-group">
              <button type="submit" disabled={loading}>
                {loading ? "検証中..." : "登録を完了"}
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: "15px" }}>
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#667eea",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "14px"
                  }}
                >
                  ワンタイムパスワードを再送信
                </button>
              ) : (
                <div>
                  <span style={{ color: "#999", fontSize: "14px" }}>
                    再送信可能まで {formatTime(timer)}
                  </span>
                </div>
              )}
            </div>

          </form>
        )}
      </div>
    </Layout>
  );
};

export default Register;
